/**
 * Icon Registry and Asset Management System
 * Centralized configuration for all icon assets in the Rainbow Bridge application
 */

// Icon registry mapping icon names to their assets and properties
export const iconRegistry = {
  // Navigation icons (existing PNG assets - preserved as per design)
  navigation: {
    home: {
      type: 'png',
      src: '/icons/home.png',
      alt: 'Home',
      category: 'navigation',
      description: 'Navigate to home screen'
    },
    journal: {
      type: 'png', 
      src: '/icons/journal.png',
      alt: 'Journal',
      category: 'navigation',
      description: 'Access journal features'
    },
    breathe: {
      type: 'png',
      src: '/icons/breathe.png', 
      alt: 'Breathe',
      category: 'navigation',
      description: 'Breathing exercises'
    },
    memorial: {
      type: 'png',
      src: '/icons/memorial.png',
      alt: 'Memorial',
      category: 'navigation',
      description: 'Memorial and remembrance section'
    },
    'pet-paw': {
      type: 'png',
      src: '/icons/pet-paw.png',
      alt: 'Pet Paw',
      category: 'navigation',
      description: 'Pet-related features'
    }
  },

  // Emotional state icons (PNG with color theming)
  emotional: {
    heart: {
      type: 'png',
      src: '/icons/hearticon.png',
      alt: 'Heart - Cherished Memories',
      category: 'emotional',
      associatedColor: '#FFB6C1',
      emotionalState: 'memories',
      description: 'Represents cherished memories and love'
    },
    teardrop: {
      type: 'png',
      src: '/icons/dropicon.png',
      alt: 'Teardrop - Grief & Loss',
      category: 'emotional',
      associatedColor: '#9370DB',
      emotionalState: 'grief',
      description: 'Represents grief, loss, and sadness'
    },
    cloud: {
      type: 'png',
      src: '/icons/cloudicon.png',
      alt: 'Cloud - Guilt or Regrets', 
      category: 'emotional',
      associatedColor: '#4682B4',
      emotionalState: 'guilt',
      description: 'Represents guilt, regrets, and difficult emotions'
    },
    star: {
      type: 'png',
      src: '/icons/staricon.png',
      alt: 'Star - Continuing Bonds',
      category: 'emotional', 
      associatedColor: '#32CD32',
      emotionalState: 'bonds',
      description: 'Represents continuing bonds and connection'
    },
    sprout: {
      type: 'png',
      src: '/icons/sprouticon.png',
      alt: 'Sprout - Gratitude & Growth',
      category: 'emotional',
      associatedColor: '#FFD700',
      emotionalState: 'gratitude',
      description: 'Represents gratitude, growth, and healing'
    }
  },

  // Functional icons (mix of SVG and existing PNG assets)
  functional: {
    // SVG functional icons (new unified design)
    upload: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M10 2l-4 4h2.5v6h3V6H14l-4-4z M4 14h12v2H4v-2z',
      alt: 'Upload',
      category: 'functional',
      description: 'Upload files or content'
    },
    loading: {
      type: 'svg', 
      viewBox: '0 0 20 20',
      path: 'M10 2v4c2.2 0 4 1.8 4 4s-1.8 4-4 4v4c4.4 0 8-3.6 8-8s-3.6-8-8-8z',
      alt: 'Loading',
      category: 'functional',
      animate: true,
      description: 'Loading or processing indicator'
    },
    success: {
      type: 'svg',
      viewBox: '0 0 20 20', 
      path: 'M10 18c4.4 0 8-3.6 8-8s-3.6-8-8-8s-8 3.6-8 8s3.6 8 8 8z M8.5 12.5l-2-2l-1.5 1.5l3.5 3.5l7-7l-1.5-1.5l-5.5 5.5z',
      alt: 'Success',
      category: 'functional',
      description: 'Success or completion indicator'
    },
    settings: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M10 12c1.1 0 2-0.9 2-2s-0.9-2-2-2s-2 0.9-2 2s0.9 2 2 2z M18 10c0-0.8-0.6-1.4-1.4-1.4c-0.1-0.5-0.3-1-0.5-1.4l1-1c0.6-0.6 0.6-1.5 0-2.1s-1.5-0.6-2.1 0l-1 1c-0.4-0.2-0.9-0.4-1.4-0.5C12.4 3.6 11.8 3 11 3H9c-0.8 0-1.4 0.6-1.4 1.4c-0.5 0.1-1 0.3-1.4 0.5l-1-1c-0.6-0.6-1.5-0.6-2.1 0s-0.6 1.5 0 2.1l1 1c-0.2 0.4-0.4 0.9-0.5 1.4C2.6 7.6 2 8.2 2 9v2c0 0.8 0.6 1.4 1.4 1.4c0.1 0.5 0.3 1 0.5 1.4l-1 1c-0.6 0.6-0.6 1.5 0 2.1s1.5 0.6 2.1 0l1-1c0.4 0.2 0.9 0.4 1.4 0.5C7.6 16.4 8.2 17 9 17h2c0.8 0 1.4-0.6 1.4-1.4c0.5-0.1 1-0.3 1.4-0.5l1 1c0.6 0.6 1.5 0.6 2.1 0s0.6-1.5 0-2.1l-1-1c0.2-0.4 0.4-0.9 0.5-1.4C17.4 12.4 18 11.8 18 11v-1z',
      alt: 'Settings',
      category: 'functional',
      description: 'Settings and configuration'
    },
    close: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M15 5l-5 5l-5-5l-1.5 1.5l5 5l-5 5L5 18l5-5l5 5l1.5-1.5l-5-5l5-5L15 5z',
      alt: 'Close',
      category: 'functional',
      description: 'Close or dismiss'
    },
    sparkle: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M10 1l1.5 3.5L15 6l-3.5 1.5L10 11l-1.5-3.5L5 6l3.5-1.5L10 1z M16 10l1 2l2 1l-2 1l-1 2l-1-2l-2-1l2-1l1-2z M4 14l0.5 1l1 0.5l-1 0.5L4 17l-0.5-1L2 15.5l1-0.5L4 14z',
      alt: 'Sparkle',
      category: 'functional',
      description: 'Magic, transformation, or special effect'
    },
    camera: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M18 6h-3l-1-2H6L5 6H2c-1.1 0-2 0.9-2 2v8c0 1.1 0.9 2 2 2h16c1.1 0 2-0.9 2-2V8c0-1.1-0.9-2-2-2z M10 15c-2.8 0-5-2.2-5-5s2.2-5 5-5s5 2.2 5 5s-2.2 5-5 5z M10 11c-1.1 0-2-0.9-2-2s0.9-2 2-2s2 0.9 2 2s-0.9 2-2 2z',
      alt: 'Camera',
      category: 'functional',
      description: 'Photo capture or upload'
    },
    palette: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M10 2C5.6 2 2 5.6 2 10c0 3.3 2 6.2 4.8 7.4c0.4 0.2 0.8-0.1 0.8-0.5c0-0.3-0.2-0.6-0.5-0.7C4.8 15.2 3 12.8 3 10c0-3.9 3.1-7 7-7s7 3.1 7 7c0 1.9-0.8 3.6-2 4.8c-0.3 0.3-0.3 0.8 0 1.1c0.3 0.3 0.8 0.3 1.1 0C17.2 14.6 18 12.4 18 10c0-4.4-3.6-8-8-8z M6 9c0.6 0 1-0.4 1-1s-0.4-1-1-1s-1 0.4-1 1s0.4 1 1 1z M8 6c0.6 0 1-0.4 1-1s-0.4-1-1-1s-1 0.4-1 1s0.4 1 1 1z M12 6c0.6 0 1-0.4 1-1s-0.4-1-1-1s-1 0.4-1 1s0.4 1 1 1z M14 9c0.6 0 1-0.4 1-1s-0.4-1-1-1s-1 0.4-1 1s0.4 1 1 1z',
      alt: 'Art Palette',
      category: 'functional',
      description: 'Art, creativity, or customization'
    },
    candle: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M10 2c-0.5 0-1 0.5-1 1v1c-1.1 0-2 0.9-2 2v10c0 1.1 0.9 2 2 2h2c1.1 0 2-0.9 2-2V6c0-1.1-0.9-2-2-2V3c0-0.5-0.5-1-1-1z M10 1c0.3 0 0.5-0.2 0.5-0.5S10.3 0 10 0s-0.5 0.2-0.5 0.5S9.7 1 10 1z',
      alt: 'Candle',
      category: 'functional',
      description: 'Memorial candle or remembrance'
    },
    phone: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M5 2c-1.1 0-2 0.9-2 2v12c0 1.1 0.9 2 2 2h10c1.1 0 2-0.9 2-2V4c0-1.1-0.9-2-2-2H5z M5 4h10v10H5V4z M10 15.5c0.3 0 0.5 0.2 0.5 0.5s-0.2 0.5-0.5 0.5s-0.5-0.2-0.5-0.5s0.2-0.5 0.5-0.5z',
      alt: 'Phone',
      category: 'functional',
      description: 'Mobile device or digital content'
    },
    diamond: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M10 2l-3 4h6l-3-4z M4 6l6 12l6-12H4z M2 6h2l1-1.5L6 6H2z M14 6l1-1.5L16 6h2l-2 0z',
      alt: 'Diamond',
      category: 'functional',
      description: 'Precious gem or premium item'
    },
    plant: {
      type: 'svg',
      viewBox: '0 0 20 20',
      path: 'M10 18v-6c0-1.1 0.9-2 2-2c0.5 0 1 0.2 1.4 0.6C13.8 10.2 14 9.6 14 9c0-1.7-1.3-3-3-3c-0.8 0-1.5 0.3-2 0.8V18h1z M8 16c-0.6 0-1-0.4-1-1s0.4-1 1-1v2z M6 14c-1.1 0-2-0.9-2-2s0.9-2 2-2v4z',
      alt: 'Plant',
      category: 'functional',
      description: 'Living memorial or growth'
    },
    
    // Existing PNG functional icons (organized in registry for fallback/alternative use)
    closeicon: {
      type: 'png',
      src: '/icons/closeicon.png',
      alt: 'Close',
      category: 'functional',
      description: 'Close or dismiss (PNG version)'
    },
    cloudicon: {
      type: 'png',
      src: '/icons/cloudicon.png',
      alt: 'Cloud',
      category: 'functional',
      description: 'Cloud or weather-related (PNG version)'
    },
    dropicon: {
      type: 'png',
      src: '/icons/dropicon.png',
      alt: 'Drop',
      category: 'functional',
      description: 'Water drop or liquid (PNG version)'
    },
    hearticon: {
      type: 'png',
      src: '/icons/hearticon.png',
      alt: 'Heart',
      category: 'functional',
      description: 'Heart or love indicator (PNG version)'
    },
    loadingicon: {
      type: 'png',
      src: '/icons/loadingicon.png',
      alt: 'Loading',
      category: 'functional',
      animate: true,
      description: 'Loading indicator (PNG version)'
    },
    settingicon: {
      type: 'png',
      src: '/icons/settingicon.png',
      alt: 'Settings',
      category: 'functional',
      description: 'Settings and configuration (PNG version)'
    },
    sprouticon: {
      type: 'png',
      src: '/icons/sprouticon.png',
      alt: 'Sprout',
      category: 'functional',
      description: 'Growth or plant sprout (PNG version)'
    },
    staricon: {
      type: 'png',
      src: '/icons/staricon.png',
      alt: 'Star',
      category: 'functional',
      description: 'Star or rating indicator (PNG version)'
    },
    successicon: {
      type: 'png',
      src: '/icons/successicon.png',
      alt: 'Success',
      category: 'functional',
      description: 'Success indicator (PNG version)'
    },
    uploadicon: {
      type: 'png',
      src: '/icons/uploadicon.png',
      alt: 'Upload',
      category: 'functional',
      description: 'Upload files or content (PNG version)'
    }
  }
};

// Color system for themed icons
export const iconColors = {
  primary: '#87CEEB',
  secondary: '#98D8E8', 
  accent: '#4A90E2',
  neutral: '#666666',
  light: '#B0C4DE',
  emotional: {
    memories: '#FFB6C1',
    grief: '#9370DB', 
    guilt: '#4682B4',
    messages: '#FF8C00',
    hope: '#FFD700'
  }
};

// Size variants for consistent scaling with responsive breakpoints
export const sizeVariants = {
  xs: { 
    width: 12, 
    height: 12,
    responsive: {
      sm: { width: 14, height: 14 },
      md: { width: 16, height: 16 },
      lg: { width: 18, height: 18 }
    }
  },
  sm: { 
    width: 16, 
    height: 16,
    responsive: {
      sm: { width: 18, height: 18 },
      md: { width: 20, height: 20 },
      lg: { width: 22, height: 22 }
    }
  },
  md: { 
    width: 20, 
    height: 20,
    responsive: {
      sm: { width: 22, height: 22 },
      md: { width: 24, height: 24 },
      lg: { width: 26, height: 26 }
    }
  },
  lg: { 
    width: 24, 
    height: 24,
    responsive: {
      sm: { width: 26, height: 26 },
      md: { width: 28, height: 28 },
      lg: { width: 30, height: 30 }
    }
  },
  xl: { 
    width: 30, 
    height: 30,
    responsive: {
      sm: { width: 32, height: 32 },
      md: { width: 34, height: 34 },
      lg: { width: 36, height: 36 }
    }
  }
};

// Accessibility configuration
export const accessibilityConfig = {
  // Color contrast ratios for WCAG compliance
  contrastRatios: {
    normal: 4.5,  // WCAG AA standard
    large: 3.0,   // WCAG AA for large text
    enhanced: 7.0 // WCAG AAA standard
  },
  
  // Reduced motion preferences
  reducedMotion: {
    respectPreference: true,
    fallbackDuration: 0.1, // Minimal animation duration when motion is reduced
    disableRotation: true,  // Disable rotation animations
    disableScale: false     // Keep scale animations as they're less problematic
  },
  
  // Screen reader optimizations
  screenReader: {
    includeCategory: true,    // Include category in aria-label
    includeDescription: true, // Include description when available
    fallbackText: 'Icon'     // Fallback when no alt text available
  }
};

// Utility functions for icon management
export const iconUtils = {
  /**
   * Find an icon by name across all categories
   * @param {string} iconName - The name of the icon to find
   * @returns {object|null} - Icon data or null if not found
   */
  findIcon: (iconName) => {
    for (const category of Object.values(iconRegistry)) {
      if (category[iconName]) {
        return category[iconName];
      }
    }
    return null;
  },

  /**
   * Get all icons from a specific category
   * @param {string} categoryName - The category to retrieve icons from
   * @returns {object} - Object containing all icons in the category
   */
  getIconsByCategory: (categoryName) => {
    return iconRegistry[categoryName] || {};
  },

  /**
   * Get all available icon names
   * @returns {string[]} - Array of all icon names
   */
  getAllIconNames: () => {
    const names = [];
    for (const category of Object.values(iconRegistry)) {
      names.push(...Object.keys(category));
    }
    return names;
  },

  /**
   * Get icons by type (svg or png)
   * @param {string} type - The type of icons to retrieve ('svg' or 'png')
   * @returns {object} - Object containing icons of the specified type
   */
  getIconsByType: (type) => {
    const result = {};
    for (const [categoryName, category] of Object.entries(iconRegistry)) {
      for (const [iconName, iconData] of Object.entries(category)) {
        if (iconData.type === type) {
          if (!result[categoryName]) result[categoryName] = {};
          result[categoryName][iconName] = iconData;
        }
      }
    }
    return result;
  },

  /**
   * Validate icon exists and return fallback if needed
   * @param {string} iconName - The name of the icon to validate
   * @param {string} fallbackIcon - Fallback icon name if primary not found
   * @returns {object} - Icon data (primary or fallback)
   */
  validateIcon: (iconName, fallbackIcon = null) => {
    const icon = iconUtils.findIcon(iconName);
    if (icon) return icon;
    
    if (fallbackIcon) {
      const fallback = iconUtils.findIcon(fallbackIcon);
      if (fallback) {
        console.warn(`Icon "${iconName}" not found, using fallback "${fallbackIcon}"`);
        return fallback;
      }
    }
    
    console.warn(`Icon "${iconName}" not found in registry`);
    return null;
  },

  /**
   * Get color for an icon based on its properties and theme
   * @param {object} iconData - The icon data object
   * @param {string} colorTheme - The requested color theme
   * @returns {string} - The color value to use
   */
  getIconColor: (iconData, colorTheme) => {
    // Use associated color if available (for emotional icons)
    if (iconData.associatedColor) {
      return iconData.associatedColor;
    }
    
    // Check emotional color mapping
    if (iconColors.emotional[colorTheme]) {
      return iconColors.emotional[colorTheme];
    }
    
    // Use standard color or fallback to the theme string
    return iconColors[colorTheme] || colorTheme;
  },

  /**
   * Get responsive dimensions for an icon based on screen size
   * @param {string} size - Base size variant (xs, sm, md, lg, xl)
   * @param {string} breakpoint - Screen size breakpoint (sm, md, lg)
   * @returns {object} - Width and height dimensions
   */
  getResponsiveDimensions: (size, breakpoint = null) => {
    const sizeConfig = sizeVariants[size];
    if (!sizeConfig) return { width: 20, height: 20 }; // Default fallback
    
    if (breakpoint && sizeConfig.responsive && sizeConfig.responsive[breakpoint]) {
      return sizeConfig.responsive[breakpoint];
    }
    
    return { width: sizeConfig.width, height: sizeConfig.height };
  },

  /**
   * Generate comprehensive aria-label for accessibility
   * @param {object} iconData - The icon data object
   * @param {string} customAlt - Custom alt text override
   * @returns {string} - Complete aria-label text
   */
  generateAriaLabel: (iconData, customAlt = null) => {
    const config = accessibilityConfig.screenReader;
    let label = customAlt || iconData.alt || config.fallbackText;
    
    if (config.includeCategory && iconData.category) {
      label += ` (${iconData.category} icon)`;
    }
    
    if (config.includeDescription && iconData.description) {
      label += ` - ${iconData.description}`;
    }
    
    return label;
  },

  /**
   * Check if user prefers reduced motion
   * @returns {boolean} - True if reduced motion is preferred
   */
  prefersReducedMotion: () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  /**
   * Get animation configuration based on motion preferences
   * @param {boolean} animate - Whether animation is requested
   * @param {string} iconName - Name of the icon (for specific handling)
   * @returns {object} - Animation configuration
   */
  getAnimationConfig: (animate, iconName = '') => {
    const config = accessibilityConfig.reducedMotion;
    const reducedMotion = iconUtils.prefersReducedMotion();
    
    if (!animate || (reducedMotion && config.respectPreference)) {
      return {
        animate: false,
        duration: config.fallbackDuration,
        disableRotation: config.disableRotation,
        disableScale: config.disableScale && reducedMotion
      };
    }
    
    return {
      animate: true,
      duration: iconName === 'loading' ? 1 : 0.3,
      disableRotation: false,
      disableScale: false
    };
  },

  /**
   * Calculate color contrast ratio (simplified implementation)
   * @param {string} foreground - Foreground color (hex)
   * @param {string} background - Background color (hex, defaults to white)
   * @returns {number} - Contrast ratio
   */
  calculateContrastRatio: (foreground, background = '#FFFFFF') => {
    // Convert hex to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    // Calculate relative luminance
    const getLuminance = (rgb) => {
      const { r, g, b } = rgb;
      const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    };
    
    const fgRgb = hexToRgb(foreground);
    const bgRgb = hexToRgb(background);
    
    if (!fgRgb || !bgRgb) return 1; // Fallback for invalid colors
    
    const fgLum = getLuminance(fgRgb);
    const bgLum = getLuminance(bgRgb);
    
    const lighter = Math.max(fgLum, bgLum);
    const darker = Math.min(fgLum, bgLum);
    
    return (lighter + 0.05) / (darker + 0.05);
  },

  /**
   * Validate color contrast compliance
   * @param {string} color - Icon color to test
   * @param {string} background - Background color (defaults to white)
   * @param {string} size - Icon size for determining compliance level
   * @returns {object} - Compliance status and ratio
   */
  validateColorContrast: (color, background = '#FFFFFF', size = 'md') => {
    const ratio = iconUtils.calculateContrastRatio(color, background);
    const config = accessibilityConfig.contrastRatios;
    
    // Large icons (lg, xl) have more lenient requirements
    const isLarge = ['lg', 'xl'].includes(size);
    const requiredRatio = isLarge ? config.large : config.normal;
    
    return {
      ratio: Math.round(ratio * 100) / 100,
      compliant: ratio >= requiredRatio,
      level: ratio >= config.enhanced ? 'AAA' : ratio >= config.normal ? 'AA' : 'Fail',
      requiredRatio
    };
  }
};

// Export default registry for backward compatibility
export default iconRegistry;